import { useEffect } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, type Toast } from '@/stores/toast.store';
import { cn } from '@/lib/utils';

/**
 * Toaster — renders toast stack at fixed position (bottom-right).
 * Mount once at the app root.
 */
export function Toaster(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed z-50 bottom-4 right-4 flex flex-col gap-2 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastCard({ toast }: { toast: Toast }): JSX.Element {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (!toast.durationMs || toast.durationMs <= 0) return;
    const id = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(id);
  }, [toast.durationMs, toast.id, dismiss]);

  const isVip = toast.variant === 'vip';

  const tone = isVip
    ? 'bg-gradient-to-r from-sky-100 to-lavender-100 border-sky-300 text-sky-900'
    : toast.variant === 'success'
      ? 'bg-mint-50 border-mint-200 text-mint-900'
      : toast.variant === 'warning'
        ? 'bg-peach-50 border-peach-200 text-peach-900'
        : toast.variant === 'error'
          ? 'bg-rose-50 border-rose-200 text-rose-900'
          : 'bg-sky-50 border-sky-200 text-sky-900';

  const Icon =
    toast.variant === 'success'
      ? CheckCircle2
      : toast.variant === 'warning'
        ? AlertTriangle
        : toast.variant === 'error'
          ? AlertCircle
          : Info;

  const clickable = !!toast.onClick;
  const handleCardClick = (e: React.MouseEvent): void => {
    if (!toast.onClick) return;
    // Only fire when the click landed on the card body, not on the dismiss X.
    if ((e.target as HTMLElement).closest('button[data-toast-dismiss]')) return;
    toast.onClick();
    dismiss(toast.id);
  };

  return (
    <div
      role={clickable ? 'button' : 'status'}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? handleCardClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toast.onClick?.();
                dismiss(toast.id);
              }
            }
          : undefined
      }
      className={cn(
        isVip
          ? 'pointer-events-auto rounded-lg border shadow-elevated px-5 py-4 flex flex-col gap-3 animate-slide-down'
          : 'pointer-events-auto rounded-lg border shadow-elevated px-3 py-2.5 flex items-start gap-2.5 animate-slide-down',
        clickable && 'cursor-pointer hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40 transition-shadow',
        tone,
      )}
    >
      {isVip ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-lg font-semibold leading-tight">{toast.title}</p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              data-toast-dismiss
              aria-label="Dismiss notification"
              className="text-current/60 hover:text-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current rounded shrink-0 mt-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {toast.description && (
            <p className="text-sm leading-relaxed">{toast.description}</p>
          )}
        </>
      ) : (
        <>
          <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">{toast.title}</p>
            {toast.description && (
              <p className="text-xs mt-0.5 leading-relaxed">{toast.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss notification"
            className="text-current/60 hover:text-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current rounded shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </div>
  );
}
